/**
 * Host allowlist guard.
 *
 * The app is served as a static SPA (pm2 `serve`), which answers on any Host
 * header. Without a check, the application loads under arbitrary hostnames,
 * enabling domain masquerading / host header injection. This module validates
 * `window.location.hostname` against an explicit allowlist so the app only runs
 * on domains it knows about; every other host is denied before the app mounts.
 *
 * The allowlist is exact-match only (no wildcard subdomains), case-insensitive.
 * It can be overridden via the comma-separated `REACT_APP_ALLOWED_HOSTS` env
 * var; otherwise the known production domains below are used. In development,
 * local hosts are allowed as well.
 */

const DEFAULT_ALLOWED_HOSTS = [
  'trends.app.buzzmonitor.com',
  'trends.app.buzzmonitor.com.br',
];

const DEVELOPMENT_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

function isDevelopment(): boolean {
  return process.env.REACT_APP_ENV !== 'production';
}

function getAllowedHosts(): string[] {
  const fromEnv = (process.env.REACT_APP_ALLOWED_HOSTS || '')
    .split(',')
    .map(value => value.trim().toLowerCase())
    .filter(Boolean);

  const base = fromEnv.length > 0 ? fromEnv : DEFAULT_ALLOWED_HOSTS.slice();

  if (isDevelopment()) {
    return base.concat(DEVELOPMENT_HOSTS);
  }

  return base;
}

export function getCurrentHostname(): string {
  if (typeof window === 'undefined' || !window.location) return '';
  // `hostname` excludes port and is already lowercased by the browser.
  return window.location.hostname.toLowerCase();
}

export function isAllowedHost(hostname: string = getCurrentHostname()): boolean {
  if (!hostname) return false;
  return getAllowedHosts().includes(hostname);
}
